import { NotesTargetPanel } from '@/components/notes/notes-target-panel';
import { ApiError, cellsApi, matricesApi, matrixRegenerationApi } from '@/lib/api';
import type { NoteViewer, NotesScope } from '@/lib/notes-queries';
import { extractComment, replaceComment } from '@/lib/regen-prompt-comment';
import { useDebouncedCallback } from '@/lib/use-debounced-callback';
import { cn } from '@/lib/utils';
import type {
  ActionLogEntry,
  ActionType,
  Cell,
  CellVersionOrigin,
  Dimension,
  NoteTarget,
  PalierNumber,
  RegenerationBatch,
  RegenerationScopeSelector,
  ResolvedPalier,
  SingularityLevier,
  UserRole,
} from '@mss_creator/shared';
import { REGENERATION_PROMPT_SEPARATOR, palierPosition } from '@mss_creator/shared';
import { Button, Icon, TextArea } from '@pms-digital/design-system';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { forwardRef, useEffect, useRef, useState } from 'react';
import { resolveColumnLabel, resolveRowLabel } from './matrix-labels';
import { RegenPromptEditor } from './regen-prompt-editor';
import { type DrawerTab, type Selection, cellsInScope } from './selection';

/**
 * What the Notes tab needs (issue #19, planche 09): the spot the selection
 * carries notes for and everything the shared notes panel asks for. Absent, the
 * drawer has two tabs, as before.
 */
export interface DrawerNotes {
  /** The matrix when nothing is selected, a cell, a row or a column; `null` for a multiple selection or the activation block. */
  target: NoteTarget | null;
  /** The target as the panel words it (« Suivi après la visite × Barre des minimas »). */
  targetLabel: string;
  /** Open discussions on the target: the badge of the tab. */
  count: number;
  scope: NotesScope;
  companyId: string;
  companyName: string;
  viewer: NoteViewer;
  /** Bumped by « Ajouter une note »: the field takes the focus. Zero until then. */
  focusComposerKey: number;
  /** « Toutes les notes de la matrice »: opens the Notes view. */
  onOpenAllNotes: () => void;
}

export interface MatrixDrawerProps {
  matrixId: string;
  selection: Selection;
  cells: Cell[];
  dimensions: Dimension[];
  singularitySetup: SingularityLevier[];
  paliers: ResolvedPalier[];
  /** Controlled from the route so a grid "..." menu can open it (M7). */
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  activeTab: DrawerTab;
  onTabChange: (tab: DrawerTab) => void;
  /** Polled by the route (2026-08-14 design doc) — `null` if no batch has ever run for this matrix. */
  latestRegenerationBatch: RegenerationBatch | null;
  /** Transient "regeneration just finished" text (I7) — shown only while the drawer is open; the route clears it after a few seconds. */
  regenerationNotice: string | null;
  /**
   * Gates the prompt reveal (issue #6) — `pms_admin` only, and the control is
   * not rendered at all otherwise. The repo's convention: a backend 403 plus a
   * hidden entry point, no frontend route guard.
   */
  role: UserRole;
  /** The Notes tab (issue #19). */
  notes?: DrawerNotes;
}

/** The drawer's own `Selection` in the wire shape the regeneration API speaks — `cells` is `multiCell` there. */
function toScopeSelector(selection: Selection): RegenerationScopeSelector {
  switch (selection.scope) {
    case 'matrix':
      return { scope: 'matrix' };
    case 'activationBlock':
      return { scope: 'activationBlock' };
    case 'row':
      return { scope: 'row', rowType: selection.rowType, rowRef: selection.rowRef };
    case 'column':
      return { scope: 'column', columnNumber: selection.columnNumber as PalierNumber };
    case 'cell':
      return { scope: 'cell', cellId: selection.cellId };
    case 'cells':
      return { scope: 'multiCell', cellIds: selection.cellIds };
    default:
      return selection satisfies never;
  }
}

/** Turns a failed prompt composition into French — a 403 is a real outcome here, not a bug (the route is pms_admin-only). */
function toPromptFetchError(error: unknown): string {
  if (error instanceof ApiError && error.code === 'ROLE_FORBIDDEN') {
    return "Vous n'avez pas les droits pour afficher ce prompt.";
  }
  return 'Impossible de composer le prompt de régénération pour le moment.';
}

/** How long the prompt sits still before the Commentaire field is re-read from it — long enough not to fight a typist, short enough to feel live. */
const COMMENT_SYNC_DEBOUNCE_MS = 400;

/**
 * Turns a failed `POST /matrices/:id/regenerate-*` into a short French
 * message, mirroring `toGenerationSubmitError` in `matrices.$matrixId.tsx`
 * (I9) — backend error messages are English, so they aren't surfaced
 * verbatim; this UI is French-only.
 */
function toRegenerationSubmitError(error: unknown): string {
  if (error instanceof ApiError && error.status === 409) {
    return 'Une régénération est déjà en cours pour cette matrice.';
  }
  return 'Impossible de lancer la régénération. Réessayez.';
}

/**
 * Which history the Historique tab shows (M8). `selection` follows the grid
 * (cell/row/column/matrix/multi-cell — the `cell_versions` of every in-scope
 * cell); `global` is deliberately decoupled from the grid and renders the raw
 * `actions` log instead.
 */
type HistoryScope = 'selection' | 'global';

/** Per-version provenance label (M10), wording from mockup Écran 28. */
const ORIGIN_LABELS: Record<CellVersionOrigin, string> = {
  seed: 'génération initiale',
  manual: 'modifiée manuellement',
  regenerated: 'régénérée',
};

/** Action-log entry labels for the global scope. */
const ACTION_TYPE_LABELS: Record<ActionType, string> = {
  cell_edit: 'Modification manuelle',
  regenerate: 'Régénération',
  resize: 'Redimensionnement',
  toggle_visibility: 'Affichage modifié',
};

function describeSelection(
  selection: Selection,
  singularitySetup: SingularityLevier[],
  dimensions: Dimension[],
  paliers: ResolvedPalier[],
): string {
  switch (selection.scope) {
    case 'matrix':
      return 'toute la matrice';
    case 'column':
      return `la colonne « Palier ${palierPosition(selection.columnNumber as PalierNumber)} · ${resolveColumnLabel(selection.columnNumber, paliers)} »`;
    case 'row':
      return `la ligne « ${resolveRowLabel(selection.rowType, selection.rowRef, singularitySetup, dimensions)} »`;
    case 'cell':
      return 'la cellule sélectionnée';
    case 'cells':
      return `${selection.cellIds.length} cellules sélectionnées`;
    case 'activationBlock':
      return "le bloc d'activation";
    default:
      return 'la sélection actuelle';
  }
}

function formatTimestamp(isoDate: string): string {
  return new Date(isoDate).toLocaleString('fr-FR');
}

/**
 * Collapsible right-side drawer (D6 — closed by default, opened via a handle
 * fixed to the right edge). Two tabs: **Historique** (renamed from "Versions"
 * by M8) and "Régénérer avec commentaire" (D3-D5, D8/M2 — still the only
 * regenerate affordance on the page).
 *
 * It had a third, **Contexte** (D21), until issue #3: editing the sujet, the
 * contexte, the documents and three digests through a 384px column was the
 * narrowest surface in the app doing the widest job, and it was the only tab
 * that ignored the grid selection the other two are built around. That editor
 * now lives in the full-width Cadrage tab (`?vue=cadrage`), and `?tab=contexte`
 * redirects there. Two editors of the same fields would have meant two
 * "Enregistrer" buttons and two staleness triggers.
 *
 * A real flex sibling of the grid (E10), not a `fixed` overlay: closed, it's a
 * thin always-reachable handle strip; open, it's a fixed-width `<aside>` that
 * shrinks the grid's available width rather than floating on top of it.
 *
 * Open/closed and the active tab are **controlled by the route** (M7), so a
 * grid "..." menu can open the drawer straight onto the tab it wants.
 */
export const MatrixDrawer = forwardRef<HTMLDivElement, MatrixDrawerProps>(function MatrixDrawer(
  {
    matrixId,
    selection,
    cells,
    dimensions,
    singularitySetup,
    paliers,
    isOpen,
    onOpenChange,
    activeTab,
    onTabChange,
    latestRegenerationBatch,
    regenerationNotice,
    role,
    notes,
  },
  ref,
) {
  const queryClient = useQueryClient();
  const [comment, setComment] = useState('');
  const [historyScope, setHistoryScope] = useState<HistoryScope>('selection');
  const [batchRestoreNotice, setBatchRestoreNotice] = useState<string | null>(null);

  // ── The prompt reveal (issue #6) ──────────────────────────────────────────
  // All of it ephemeral component state: an edited prompt is consumed by the
  // submit that follows it and never persisted. After a batch lands, the next
  // composition starts from the server's fresh assembly again.
  const [promptRevealed, setPromptRevealed] = useState(false);
  const [promptBody, setPromptBody] = useState('');
  const [promptInstructions, setPromptInstructions] = useState('');
  const [scopePromptName, setScopePromptName] = useState<string | null>(null);
  const [scopePromptVersion, setScopePromptVersion] = useState<number | null>(null);
  /** The last text the server composed — what "edited by hand" is measured against. `null` until the first composition. */
  const [lastComposedBody, setLastComposedBody] = useState<string | null>(null);
  const [promptFetchError, setPromptFetchError] = useState<string | null>(null);
  const [pendingPromptConflict, setPendingPromptConflict] = useState(false);
  /** True once the operator has removed or renamed the comment heading — the field has nowhere to write. */
  const [commentAnchorLost, setCommentAnchorLost] = useState(false);
  /**
   * Bumped when the selection moves under a revealed, untouched prompt; the
   * effect below is what actually recomposes.
   *
   * The selection change is detected during render (the adjust-state-during-
   * render pattern the Historique scope already uses), but firing the request
   * there would fire it twice under StrictMode's double render. A ticket plus
   * an effect keeps the detection where it belongs and the network call where
   * React expects one.
   */
  const [recomposeTicket, setRecomposeTicket] = useState(0);
  const pendingComposeCommentRef = useRef<string | null>(null);

  const canEditPrompt = role === 'pms_admin';
  const promptManuallyEdited = lastComposedBody !== null && promptBody !== lastComposedBody;

  const composePromptMutation = useMutation({
    mutationFn: (input: { comment: string }) =>
      matrixRegenerationApi.getPrompt(matrixId, {
        selector: toScopeSelector(selection),
        ...(input.comment.trim() ? { comment: input.comment } : {}),
      }),
    onSuccess: (preview) => {
      setPromptBody(preview.body);
      setLastComposedBody(preview.body);
      setPromptInstructions(preview.instructions);
      setScopePromptName(preview.scopePromptName);
      setScopePromptVersion(preview.scopePromptVersion);
      setPromptFetchError(null);
      setCommentAnchorLost(false);
    },
    onError: (error) => setPromptFetchError(toPromptFetchError(error)),
  });

  /**
   * Composes against an explicitly-passed comment rather than the `comment`
   * state, because the two callers that matter run right after setting it —
   * a successful batch clears it, and the conflict resolution recomposes from
   * whatever the field holds. Reading the state here would compose against its
   * previous value.
   */
  function composePrompt(forComment: string) {
    setPendingPromptConflict(false);
    setPromptFetchError(null);
    composePromptMutation.mutate({ comment: forComment });
  }

  /** Discards any composition — the next reveal starts from a fresh one rather than showing a prompt built for something else. */
  function resetPrompt() {
    setPromptBody('');
    setLastComposedBody(null);
    setPromptInstructions('');
    setScopePromptName(null);
    setScopePromptVersion(null);
    setPendingPromptConflict(false);
    setPromptFetchError(null);
    setCommentAnchorLost(false);
  }

  // Reset the Historique tab back to the grid-driven scope whenever the
  // selection changes — a "..." menu click on a cell (M7) must land on that
  // cell's history, not on whatever global view was left open. Adjusting state
  // during render (React's documented alternative to a `useEffect` sync).
  const [lastSelection, setLastSelection] = useState<Selection>(selection);
  if (selection !== lastSelection) {
    setLastSelection(selection);
    setHistoryScope('selection');
    // The revealed prompt was composed for the *previous* selection, and its
    // coherence context and output contract both name those cells. Recompose —
    // unless the operator has edited it, in which case they are asked instead
    // of having their work thrown away or silently sent against the wrong
    // cells. Same rule, same wording as the wizard's récapitulatif.
    if (!promptRevealed || lastComposedBody === null) {
      resetPrompt();
    } else if (promptManuallyEdited) {
      setPendingPromptConflict(true);
    } else {
      pendingComposeCommentRef.current = comment;
      setRecomposeTicket((ticket) => ticket + 1);
    }
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: keyed on the ticket alone — the comment to compose against is captured in the ref at the moment the selection changed, and `composePrompt` is re-created every render, so watching it would re-fire this on every unrelated update.
  useEffect(() => {
    if (recomposeTicket === 0) return;
    const forComment = pendingComposeCommentRef.current;
    if (forComment === null) return;
    pendingComposeCommentRef.current = null;
    composePrompt(forComment);
  }, [recomposeTicket]);

  function handleToggleReveal() {
    const next = !promptRevealed;
    setPromptRevealed(next);
    if (next && lastComposedBody === null && !composePromptMutation.isPending) {
      composePrompt(comment);
    }
  }

  /**
   * Prompt → field, on a debounce rather than per keystroke: re-reading the
   * whole prompt on every character would fight the typist's cursor in the
   * Commentaire field for no gain.
   *
   * When the anchor is gone the field is emptied and disabled rather than
   * left showing a comment that is no longer anywhere in the prompt — and it
   * comes back on its own as soon as a recognisable heading reappears.
   */
  const syncCommentFromPrompt = useDebouncedCallback((nextBody: string) => {
    const extracted = extractComment(nextBody);
    if (extracted === null) {
      setComment('');
      setCommentAnchorLost(true);
      return;
    }
    setComment(extracted);
    setCommentAnchorLost(false);
  }, COMMENT_SYNC_DEBOUNCE_MS);

  function handlePromptBodyChange(nextBody: string) {
    setPromptBody(nextBody);
    syncCommentFromPrompt(nextBody);
  }

  /**
   * Field → prompt, immediately. The baseline moves with it: a comment typed
   * in the field is not a hand-edit of the prompt — the server would compose
   * the very same section from that comment — so it must not raise the
   * conserver/recomposer question on the next selection change.
   */
  function handleCommentChange(nextComment: string) {
    setComment(nextComment);
    if (!promptRevealed || lastComposedBody === null || commentAnchorLost) return;
    setPromptBody((previous) => replaceComment(previous, nextComment));
    setLastComposedBody((previous) =>
      previous === null ? previous : replaceComment(previous, nextComment),
    );
  }

  const actionsQuery = useQuery({
    queryKey: ['actions', matrixId],
    queryFn: () => matricesApi.listActions(matrixId),
    enabled: isOpen && activeTab === 'historique' && historyScope === 'global',
  });

  const restoreMutation = useMutation({
    mutationFn: ({ cellId, versionId }: { cellId: string; versionId: string }) =>
      cellsApi.restoreVersion(cellId, versionId),
    onSuccess: (updatedCell) => {
      queryClient.setQueryData<Cell[]>(['cells', matrixId], (previous) =>
        previous?.map((cell) => (cell.id === updatedCell.id ? updatedCell : cell)),
      );
    },
  });

  /**
   * Batch-level restore (I2) — reverts every cell of a past regeneration at
   * once, skipping any whose current version is a manual edit newer than
   * the batch (reported via `batchRestoreNotice`).
   */
  const restoreBatchMutation = useMutation({
    mutationFn: (batchId: string) => matrixRegenerationApi.restoreBatch(batchId),
    onSuccess: (result) => {
      queryClient.setQueryData<Cell[]>(['cells', matrixId], (previous) => {
        if (!previous) return previous;
        const restoredById = new Map(result.restoredCells.map((cell) => [cell.id, cell]));
        return previous.map((cell) => restoredById.get(cell.id) ?? cell);
      });
      setBatchRestoreNotice(
        result.skippedCellIds.length > 0
          ? `${result.restoredCells.length} cellule(s) restaurée(s), ${result.skippedCellIds.length} ignorée(s) car modifiée(s) manuellement depuis.`
          : `${result.restoredCells.length} cellule(s) restaurée(s).`,
      );
    },
  });

  /**
   * The regen tab's submit (2026-08-14 design doc) — dispatches to whichever
   * of the 6 scope-specific routes matches the current grid selection.
   * Applies without confirmation (I6/I7): success just repoints the shared
   * `['regeneration-batch', matrixId]` query the route polls, which is what
   * drives the disabled-submit state, the toast/in-drawer notice, and the
   * grid's corner badges.
   */
  const regenerateMutation = useMutation({
    mutationFn: async (): Promise<RegenerationBatch> => {
      const trimmedComment = comment.trim() ? comment : undefined;
      // What is on screen is what is sent (issue #6): the edited body and the
      // locked contract, joined the way the server joins them. Sent whenever a
      // composition exists, edited or not — the server compares it against its
      // own assembly to decide whether the batch is flagged as hand-edited.
      //
      // Keyed on the composition rather than on `promptRevealed`, so that
      // revealing, editing, then collapsing the panel does not quietly throw
      // the edit away and send the plain assembly instead. Collapsing hides a
      // prompt; it does not retract it. A selection change while collapsed
      // does discard it (see the selection branch above), which is the case
      // where the composition genuinely no longer describes anything.
      const shownPrompt =
        lastComposedBody === null
          ? undefined
          : `${promptBody}${REGENERATION_PROMPT_SEPARATOR}${promptInstructions}`;
      const base = {
        ...(trimmedComment === undefined ? {} : { comment: trimmedComment }),
        ...(shownPrompt === undefined ? {} : { prompt: shownPrompt }),
      };
      switch (selection.scope) {
        case 'matrix':
          return matrixRegenerationApi.regenerateMatrix(matrixId, base);
        case 'row':
          return matrixRegenerationApi.regenerateRow(matrixId, {
            rowType: selection.rowType,
            rowRef: selection.rowRef,
            ...base,
          });
        case 'column':
          return matrixRegenerationApi.regenerateColumn(matrixId, {
            columnNumber: selection.columnNumber as PalierNumber,
            ...base,
          });
        case 'cell':
          return matrixRegenerationApi.regenerateCell(matrixId, {
            cellId: selection.cellId,
            ...base,
          });
        case 'cells':
          return matrixRegenerationApi.regenerateCells(matrixId, {
            cellIds: selection.cellIds,
            ...base,
          });
        case 'activationBlock':
          return matrixRegenerationApi.regenerateActivationBlock(matrixId, base);
        default:
          return selection satisfies never;
      }
    },
    onSuccess: (batch) => {
      setComment('');
      // The edit was ephemeral and has now been consumed. Back to a fresh
      // assembly, so the next batch is composed from the grid the previous one
      // just changed rather than from a prompt describing its old contents.
      if (promptRevealed) {
        composePrompt('');
      } else {
        resetPrompt();
      }
      queryClient.setQueryData(['regeneration-batch', matrixId], batch);
    },
  });
  const regenerationSubmitError = regenerateMutation.isError
    ? toRegenerationSubmitError(regenerateMutation.error)
    : null;
  const isRegenerationActive =
    latestRegenerationBatch?.status === 'pending' || latestRegenerationBatch?.status === 'running';

  const inScope = cellsInScope(selection, cells);
  const selectionLabel = describeSelection(selection, singularitySetup, dimensions, paliers);

  return (
    <div ref={ref} className="flex h-full flex-shrink-0">
      {!isOpen && (
        <button
          type="button"
          onClick={() => onOpenChange(true)}
          aria-label="Ouvrir le panneau"
          className="flex h-full w-8 flex-shrink-0 flex-col items-center justify-center bg-white text-bleu-nuit hover:bg-gray-50"
        >
          <Icon name="right_panel_open" size={16} />
        </button>
      )}

      {isOpen && (
        // No border on the left edge (2026-08-24 grill, point 6) — the grid's
        // own frame does the separating now that it hugs the table. `shadow-lg`
        // (PMS token) stays: the page background is white and so is this
        // panel, so without that soft shading along the edge facing the grid
        // the drawer would have no surface of its own at all.
        <aside className="flex h-full w-96 flex-shrink-0 flex-col bg-white shadow-lg">
          <div className="flex items-center justify-between border-gray-200 border-b-2 p-4">
            <h2 className="font-semibold text-body-lg">Panneau</h2>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => onOpenChange(false)}
              aria-label="Fermer le panneau"
            >
              <Icon name="close" size={16} />
            </Button>
          </div>

          <div className="flex border-gray-200 border-b-2">
            <button
              type="button"
              onClick={() => onTabChange('historique')}
              aria-pressed={activeTab === 'historique'}
              className={cn(
                'flex-1 px-2 py-3 font-semibold text-small',
                activeTab === 'historique'
                  ? 'border-bleu-nuit border-b-2 text-bleu-nuit'
                  : 'text-gray-500 hover:text-bleu-nuit',
              )}
            >
              Historique
            </button>
            <button
              type="button"
              onClick={() => onTabChange('regen')}
              aria-pressed={activeTab === 'regen'}
              // Three tabs share 384px: the label is shortened, its name is not.
              aria-label="Régénérer avec commentaire"
              className={cn(
                'flex-1 px-2 py-3 font-semibold text-small',
                activeTab === 'regen'
                  ? 'border-bleu-nuit border-b-2 text-bleu-nuit'
                  : 'text-gray-500 hover:text-bleu-nuit',
              )}
            >
              {notes ? 'Régénérer' : 'Régénérer avec commentaire'}
            </button>
            {notes && (
              <button
                type="button"
                onClick={() => onTabChange('notes')}
                aria-pressed={activeTab === 'notes'}
                className={cn(
                  'flex flex-1 items-center justify-center gap-1 px-2 py-3 font-semibold text-small',
                  activeTab === 'notes'
                    ? 'border-bleu-nuit border-b-2 text-bleu-nuit'
                    : 'text-gray-500 hover:text-bleu-nuit',
                )}
              >
                Notes
                {notes.count > 0 && (
                  <span
                    data-testid="drawer-notes-count"
                    className="rounded-full bg-gray-100 px-1 font-semibold text-gray-700 text-micro"
                  >
                    {notes.count}
                  </span>
                )}
              </button>
            )}
          </div>

          <div className="flex-1 overflow-auto p-4">
            {activeTab === 'historique' && (
              <div className="space-y-4">
                {/* Scope switcher (M8). The left option always mirrors the grid
                    selection (cellule/ligne/colonne/matrice/multi-sélection);
                    the right one ignores it entirely. */}
                <fieldset
                  aria-label="Portée de l'historique"
                  className="flex min-w-0 rounded-md border border-gray-200 p-0"
                >
                  <button
                    type="button"
                    onClick={() => setHistoryScope('selection')}
                    aria-pressed={historyScope === 'selection'}
                    className={cn(
                      'flex-1 rounded-md px-2 py-1 font-semibold text-caption',
                      historyScope === 'selection'
                        ? 'bg-bleu-nuit-tint text-bleu-nuit'
                        : 'text-gray-500 hover:text-bleu-nuit',
                    )}
                  >
                    Sélection
                  </button>
                  <button
                    type="button"
                    onClick={() => setHistoryScope('global')}
                    aria-pressed={historyScope === 'global'}
                    className={cn(
                      'flex-1 rounded-md px-2 py-1 font-semibold text-caption',
                      historyScope === 'global'
                        ? 'bg-bleu-nuit-tint text-bleu-nuit'
                        : 'text-gray-500 hover:text-bleu-nuit',
                    )}
                  >
                    Global
                  </button>
                </fieldset>

                {historyScope === 'selection' ? (
                  <div className="space-y-6">
                    <p className="text-caption text-gray-500">
                      Portée : {selectionLabel}. Restaurer une version en ajoute une nouvelle copie
                      en haut de l'historique — rien n'est supprimé.
                    </p>
                    {batchRestoreNotice && (
                      <p className="text-bleu-nuit text-caption">{batchRestoreNotice}</p>
                    )}

                    {inScope.length === 0 && (
                      <p className="text-gray-500 text-small">
                        Aucune cellule dans cette sélection.
                      </p>
                    )}
                    {inScope.map((cell) => (
                      <div key={cell.id} className="space-y-2">
                        <h3 className="font-semibold text-small">
                          Palier {palierPosition(cell.columnNumber as PalierNumber)} ·{' '}
                          {resolveRowLabel(cell.rowType, cell.rowRef, singularitySetup, dimensions)}
                        </h3>
                        {cell.versions.length === 0 ? (
                          <p className="text-caption text-gray-500">Jamais générée.</p>
                        ) : (
                          <ul className="space-y-2">
                            {cell.versions.map((version) => {
                              const isCurrent = version.id === cell.currentVersionId;
                              return (
                                <li
                                  key={version.id}
                                  className="rounded-md border border-gray-200 bg-gray-50 p-2 text-caption"
                                >
                                  <div className="font-semibold">
                                    {isCurrent
                                      ? `Version actuelle — ${ORIGIN_LABELS[version.origin]}`
                                      : ORIGIN_LABELS[version.origin]}
                                  </div>
                                  <div className="text-gray-500">
                                    {formatTimestamp(version.createdAt)} —{' '}
                                    {version.createdBy ?? 'Système'}
                                  </div>
                                  <div className="mt-1">
                                    {version.content.slice(0, 80)}
                                    {version.content.length > 80 ? '…' : ''}
                                  </div>
                                  {!isCurrent && (
                                    <button
                                      type="button"
                                      onClick={() =>
                                        restoreMutation.mutate({
                                          cellId: cell.id,
                                          versionId: version.id,
                                        })
                                      }
                                      disabled={restoreMutation.isPending}
                                      className="mt-1 font-semibold text-bleu-nuit underline disabled:opacity-40"
                                    >
                                      restaurer
                                    </button>
                                  )}
                                  {/* I2 — batch-level restore, offered inline next to the version it produced. */}
                                  {isCurrent &&
                                    version.origin === 'regenerated' &&
                                    version.batchId && (
                                      <button
                                        type="button"
                                        onClick={() =>
                                          restoreBatchMutation.mutate(version.batchId as string)
                                        }
                                        disabled={restoreBatchMutation.isPending}
                                        className="mt-1 block font-semibold text-bleu-nuit underline disabled:opacity-40"
                                      >
                                        restaurer cette régénération (toutes les cellules
                                        concernées)
                                      </button>
                                    )}
                                </li>
                              );
                            })}
                          </ul>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <GlobalHistory
                    entries={actionsQuery.data}
                    isLoading={actionsQuery.isLoading}
                    isError={actionsQuery.isError}
                  />
                )}
              </div>
            )}

            {activeTab === 'notes' &&
              notes &&
              (notes.target ? (
                <NotesTargetPanel
                  scope={notes.scope}
                  target={notes.target}
                  targetLabel={notes.targetLabel}
                  companyId={notes.companyId}
                  companyName={notes.companyName}
                  viewer={notes.viewer}
                  {...(notes.focusComposerKey > 0
                    ? { focusComposerKey: notes.focusComposerKey }
                    : {})}
                  onOpenAllNotes={notes.onOpenAllNotes}
                />
              ) : (
                <div className="space-y-3 text-gray-600 text-small">
                  <p>
                    Les notes ne portent ni sur une sélection multiple ni sur le bloc d'activation.
                    Sélectionnez une case, une ligne ou une colonne, ou aucune pour les notes de la
                    matrice.
                  </p>
                  <button
                    type="button"
                    onClick={notes.onOpenAllNotes}
                    className="rounded-sm text-bleu-nuit text-caption underline underline-offset-2 hover:text-bleu-nuit-deep focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit"
                  >
                    Toutes les notes de la matrice
                  </button>
                </div>
              ))}

            {activeTab === 'regen' && (
              <div className="space-y-4">
                <div className="rounded-md border border-gray-200 bg-gray-50 p-3 text-small">
                  Régénérer {selectionLabel}. Le résultat est appliqué immédiatement — vous pourrez
                  toujours restaurer une version précédente depuis l'historique.
                </div>
                <div className="space-y-1">
                  <TextArea
                    label={<span>Commentaire de régénération</span>}
                    value={comment}
                    onChange={(event) => handleCommentChange(event.target.value)}
                    placeholder="Commentaire pour orienter la régénération (optionnel)…"
                    disabled={isRegenerationActive || commentAnchorLost}
                  />
                  {commentAnchorLost && (
                    <p
                      data-testid="regen-comment-anchor-lost"
                      className="text-caption text-gray-500"
                    >
                      La section « Commentaire de l'utilisateur » n'est plus dans le prompt —
                      écrivez directement dedans.
                    </p>
                  )}
                </div>

                {canEditPrompt && (
                  <RegenPromptEditor
                    isRevealed={promptRevealed}
                    onToggleReveal={handleToggleReveal}
                    body={promptBody}
                    onBodyChange={handlePromptBodyChange}
                    instructions={promptInstructions}
                    scopePromptName={scopePromptName}
                    scopePromptVersion={scopePromptVersion}
                    isFetching={composePromptMutation.isPending}
                    fetchError={promptFetchError}
                    isManuallyEdited={promptManuallyEdited}
                    pendingConflict={pendingPromptConflict}
                    onKeepManualEdit={() => setPendingPromptConflict(false)}
                    onRecompose={() => composePrompt(comment)}
                    disabled={isRegenerationActive}
                  />
                )}

                {isOpen && regenerationNotice && (
                  <p className="text-bleu-nuit text-small">{regenerationNotice}</p>
                )}

                {regenerationSubmitError && (
                  <p className="text-danger text-small">{regenerationSubmitError}</p>
                )}

                {latestRegenerationBatch?.status === 'failed' && latestRegenerationBatch.error && (
                  <div className="text-danger text-small">
                    <p>
                      La régénération a échoué. Réessayez, ou contactez un administrateur si le
                      problème persiste.
                    </p>
                    <details className="mt-1 text-caption text-gray-500">
                      <summary className="cursor-pointer">Détails techniques</summary>
                      <p className="mt-1 whitespace-pre-wrap font-mono">
                        {latestRegenerationBatch.error}
                      </p>
                    </details>
                  </div>
                )}

                <Button
                  variant="secondary"
                  type="button"
                  onClick={() => regenerateMutation.mutate()}
                  disabled={isRegenerationActive || regenerateMutation.isPending}
                >
                  {isRegenerationActive ? 'Régénération en cours…' : 'Régénérer'}
                </Button>
              </div>
            )}
          </div>
        </aside>
      )}
    </div>
  );
});

interface GlobalHistoryProps {
  entries: ActionLogEntry[] | undefined;
  isLoading: boolean;
  isError: boolean;
}

/**
 * The raw `actions` log (M8). Only `resize`/`toggle_visibility` entries ever
 * land here this pass (E3), so the empty/explanatory copy says so out loud —
 * a consultant who edits a cell and then looks here would otherwise read the
 * absence as a bug.
 */
function GlobalHistory({ entries, isLoading, isError }: GlobalHistoryProps) {
  return (
    <div className="space-y-4">
      <p className="text-caption text-gray-500">
        Actions de mise en page de la matrice (redimensionnement, affichage). Les modifications
        manuelles et les régénérations d'une cellule apparaissent dans l'historique de cette
        cellule, via la portée « Sélection ».
      </p>
      {isLoading && <p className="text-gray-500 text-small">Chargement de l'historique…</p>}
      {isError && (
        <p className="text-danger text-small">Impossible de charger l'historique de la matrice.</p>
      )}
      {entries?.length === 0 && (
        <p className="text-gray-500 text-small">Aucune action de mise en page pour le moment.</p>
      )}
      <ul className="space-y-2">
        {entries?.map((entry) => (
          <li
            key={entry.id}
            className={cn(
              'rounded-md border p-2 text-caption',
              entry.isCurrent ? 'border-bleu-nuit bg-bleu-nuit-tint' : 'border-gray-200 bg-gray-50',
            )}
          >
            <div className="font-semibold">
              {ACTION_TYPE_LABELS[entry.actionType]}
              {entry.isCurrent && (
                <span className="ml-1 font-normal text-bleu-nuit">(position actuelle)</span>
              )}
            </div>
            <div className="text-gray-500">
              {formatTimestamp(entry.createdAt)} — {entry.actorDisplayName ?? 'Système'}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
